// src/pages/AdminPermissionsTab.jsx
// Onglet "Permissions & Rôles" dans l'admin panel
// Sections : 1) Custom Roles CRUD  2) Override par utilisateur

import React, { useEffect, useState, useCallback } from 'react';
import { Plus, Edit2, Trash2, X, Check, Save, Users, Shield, ChevronDown, ChevronUp, ToggleLeft, ToggleRight } from 'lucide-react';
import toast from 'react-hot-toast';
import { permissionsAPI } from '../services/api';
import api from '../services/api';
import { extractError } from '../context/AuthContext';

// ─── Helpers ──────────────────────────────────────────────────

function Badge({ color, children }) {
  return (
    <span style={{ whiteSpace: 'nowrap',
      display: 'inline-block', padding: '2px 8px', borderRadius: 12,
      fontSize: '0.72rem', fontWeight: 600,
      background: color + '22', color, border: `1px solid ${color}44`,
    }}>
      {children}
    </span>
  );
}

function Toggle({ value, onChange, disabled }) {
  return (
    <button
      onClick={() => !disabled && onChange(!value)}
      disabled={disabled}
      style={{
        background: 'none', border: 'none', cursor: disabled ? 'default' : 'pointer',
        padding: 2, opacity: disabled ? 0.35 : 1,
      }}
    >
      {value
        ? <ToggleRight size={26} color="#22c55e" />
        : <ToggleLeft size={26} color="var(--text-muted)" />
      }
    </button>
  );
}

// ─── Section Custom Roles ──────────────────────────────────────

function RoleFormModal({ role, catalog, onClose, onSaved }) {
  const isEdit = !!role;
  const [name, setName] = useState(role?.name || '');
  const [desc, setDesc] = useState(role?.description || '');
  const [selectedPerms, setSelectedPerms] = useState(new Set(role?.permissions || []));
  const [saving, setSaving] = useState(false);

  const toggle = (key) => {
    setSelectedPerms(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const toggleAll = (moduleKeys, force) => {
    setSelectedPerms(prev => {
      const next = new Set(prev);
      moduleKeys.forEach(k => force ? next.add(k) : next.delete(k));
      return next;
    });
  };

  const save = async () => {
    if (!name.trim()) { toast.error('Nom requis'); return; }
    setSaving(true);
    try {
      const payload = { name: name.trim(), description: desc.trim(), permissions: [...selectedPerms] };
      if (isEdit) {
        await permissionsAPI.updateRole(role.id, payload);
      } else {
        await permissionsAPI.createRole(payload);
      }
      toast.success(isEdit ? 'Rôle mis à jour' : 'Rôle créé');
      onSaved();
      onClose();
    } catch (e) { toast.error(extractError(e)); }
    finally { setSaving(false); }
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 1000,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
    }}>
      <div style={{
        background: 'var(--bg-card)', border: '1px solid var(--border)',
        borderRadius: 12, width: '100%', maxWidth: 680,
        maxHeight: '90vh', display: 'flex', flexDirection: 'column',
      }}>
        {/* Header */}
        <div style={{ padding: '20px 24px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 style={{ margin: 0, fontSize: '1.05rem' }}>{isEdit ? 'Modifier le rôle' : 'Créer un rôle custom'}</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)' }}>
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: '20px 24px', overflowY: 'auto', flex: 1 }}>
          {/* Nom & description */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 20 }}>
            <div>
              <label style={{ fontSize: '0.8rem', color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>Nom du rôle *</label>
              <input
                value={name} onChange={e => setName(e.target.value)} placeholder="ex: Consultant Senior"
                style={{ width: '100%', padding: '8px 12px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-elevated)', color: 'var(--text-primary)', boxSizing: 'border-box' }}
              />
            </div>
            <div>
              <label style={{ fontSize: '0.8rem', color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>Description</label>
              <input
                value={desc} onChange={e => setDesc(e.target.value)} placeholder="Description optionnelle"
                style={{ width: '100%', padding: '8px 12px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-elevated)', color: 'var(--text-primary)', boxSizing: 'border-box' }}
              />
            </div>
          </div>

          {/* Permissions par module */}
          <p style={{ fontSize: '0.82rem', color: 'var(--text-muted)', marginBottom: 12 }}>
            Sélectionnez les modules et actions accessibles pour ce rôle :
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {catalog.map(mod => {
              const allSelected = mod.keys.every(k => selectedPerms.has(k));
              const someSelected = mod.keys.some(k => selectedPerms.has(k));
              return (
                <div key={mod.key} style={{ border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
                  <div style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '10px 14px', background: someSelected ? 'rgba(59,130,246,0.05)' : 'var(--bg-elevated)',
                  }}>
                    <div>
                      <span style={{ fontWeight: 600, fontSize: '0.88rem' }}>{mod.label}</span>
                      {mod.description && <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginLeft: 8 }}>{mod.description}</span>}
                    </div>
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                      {someSelected && !allSelected && <Badge color="#f59e0b">Partiel</Badge>}
                      {allSelected && <Badge color="#22c55e">Tout</Badge>}
                      <button
                        onClick={() => toggleAll(mod.keys, !allSelected)}
                        style={{ fontSize: '0.72rem', padding: '3px 8px', borderRadius: 4, border: '1px solid var(--border)', background: 'var(--bg-card)', cursor: 'pointer', color: 'var(--text-muted)' }}
                      >
                        {allSelected ? 'Tout désélectionner' : 'Tout sélectionner'}
                      </button>
                    </div>
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, padding: '10px 14px', borderTop: '1px solid var(--border)' }}>
                    {mod.keys.map(k => (
                      <button
                        key={k}
                        onClick={() => toggle(k)}
                        title={k}
                        style={{
                          padding: '4px 10px', borderRadius: 6, cursor: 'pointer', fontSize: '0.78rem',
                          fontWeight: 500, transition: 'all 0.15s',
                          background: selectedPerms.has(k) ? '#3B82F6' : 'var(--bg-card)',
                          color: selectedPerms.has(k) ? 'white' : 'var(--text-muted)',
                          border: `1px solid ${selectedPerms.has(k) ? '#3B82F6' : 'var(--border)'}`,
                        }}
                      >
                        {mod.keyLabels[k] || k}
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Footer */}
        <div style={{ padding: '16px 24px', borderTop: '1px solid var(--border)', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button onClick={onClose} style={{ padding: '8px 16px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-elevated)', cursor: 'pointer', color: 'var(--text-muted)' }}>
            Annuler
          </button>
          <button
            onClick={save} disabled={saving}
            style={{ padding: '8px 18px', borderRadius: 6, border: 'none', background: '#3B82F6', color: 'white', fontWeight: 600, cursor: 'pointer', opacity: saving ? 0.6 : 1 }}
          >
            {saving ? 'Enregistrement...' : (isEdit ? 'Mettre à jour' : 'Créer le rôle')}
          </button>
        </div>
      </div>
    </div>
  );
}

function CustomRolesSection({ catalog, users, onUsersRefresh }) {
  const [roles, setRoles]         = useState([]);
  const [loading, setLoading]     = useState(true);
  const [modal, setModal]         = useState(null);
  const [addSelect, setAddSelect] = useState({});   // roleId → userId
  const [saving, setSaving]       = useState(null); // userId en cours

  const allKeyLabels = catalog.reduce((acc, mod) => ({ ...acc, ...mod.keyLabels }), {});
  const ini = n => n.split(' ').filter(Boolean).slice(0, 2).map(w => w[0].toUpperCase()).join('');

  const fetchRoles = useCallback(async () => {
    setLoading(true);
    try { const res = await permissionsAPI.listRoles(); setRoles(res.data || []); }
    catch (e) { toast.error(extractError(e)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchRoles(); }, [fetchRoles]);

  const deleteRole = async (role) => {
    if (!window.confirm(`Supprimer "${role.name}" ?`)) return;
    try { await permissionsAPI.deleteRole(role.id); toast.success('Rôle supprimé'); fetchRoles(); }
    catch (e) { toast.error(extractError(e)); }
  };

  const addMember = async (roleId, userId) => {
    if (!userId) { toast.error('Sélectionnez un utilisateur'); return; }
    setSaving(userId);
    try {
      await permissionsAPI.updateUserPerms(userId, { custom_role_id: roleId, overrides: [] });
      toast.success('Membre ajouté');
      setAddSelect(p => ({ ...p, [roleId]: '' }));
      fetchRoles();
      if (onUsersRefresh) onUsersRefresh();
    } catch (e) { toast.error(extractError(e)); }
    finally { setSaving(null); }
  };

  const removeMember = async (userId) => {
    setSaving(userId);
    try {
      await permissionsAPI.updateUserPerms(userId, { custom_role_id: null, overrides: [] });
      toast.success('Membre retiré');
      fetchRoles();
      if (onUsersRefresh) onUsersRefresh();
    } catch (e) { toast.error(extractError(e)); }
    finally { setSaving(null); }
  };

  const membersOf  = (rid) => users.filter(u => String(u.custom_role_id) === String(rid));
  const availableFor = (rid) => users.filter(u => !u.custom_role_id || String(u.custom_role_id) !== String(rid));

  return (
    <div>
      <div style={{ marginBottom: 16, padding: '10px 14px', borderRadius: 8,
        background: 'rgba(245,158,11,0.05)', border: '1px solid rgba(245,158,11,0.25)',
        fontSize: '0.8rem', color: 'var(--text-secondary)', lineHeight: 1.6 }}>
        <strong style={{ color: '#D97706' }}>Rôles custom = remplacement complet des permissions.</strong>{' '}
        Ils <strong>remplacent</strong> les accès du rôle système. Le badge organisationnel reste inchangé.
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div>
          <h3 style={{ margin: 0, fontSize: '1rem' }}>Rôles custom</h3>
          <p style={{ margin: '4px 0 0', fontSize: '0.8rem', color: 'var(--text-muted)' }}>Permissions sur mesure assignables à des utilisateurs</p>
        </div>
        <button onClick={() => setModal('new')}
          style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 6, border: 'none', background: '#3B82F6', color: 'white', fontWeight: 600, cursor: 'pointer', fontSize: '0.85rem' }}>
          <Plus size={15}/> Nouveau rôle
        </button>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 32 }}><div className="spinner"/></div>
      ) : roles.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 32, color: 'var(--text-muted)', border: '1px dashed var(--border)', borderRadius: 8 }}>
          Aucun rôle custom. Créez-en un pour affiner les accès.
        </div>
      ) : (
        <div style={{ overflowX: 'auto', border: '1px solid var(--border)', borderRadius: 10 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
            <thead>
              <tr style={{ background: 'var(--bg-elevated)', borderBottom: '2px solid var(--border)' }}>
                {['Rôle', 'Permissions', 'Membres', ''].map((h, i) => (
                  <th key={i} style={{ padding: '10px 14px', textAlign: 'left', color: 'var(--text-muted)', fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {roles.map((role, idx) => {
                const members  = membersOf(role.id);
                const available = availableFor(role.id);
                return (
                  <tr key={role.id} style={{ borderBottom: idx < roles.length - 1 ? '1px solid var(--border)' : 'none' }}>

                    {/* Nom */}
                    <td style={{ padding: '14px', verticalAlign: 'top', minWidth: 160 }}>
                      <div style={{ fontWeight: 700, fontSize: '0.9rem' }}>{role.name}</div>
                      {role.description && <div style={{ fontSize: '0.73rem', color: 'var(--text-muted)', marginTop: 2 }}>{role.description}</div>}
                    </td>

                    {/* Permissions */}
                    <td style={{ padding: '14px', verticalAlign: 'top', maxWidth: 320 }}>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                        {(!role.permissions || role.permissions.length === 0) && (
                          <span style={{ fontSize: '0.73rem', color: 'var(--text-muted)', fontStyle: 'italic' }}>Aucune</span>
                        )}
                        {(role.permissions || []).slice(0, 9).map(p => (
                          <span key={p} title={p} style={{ padding: '2px 7px', borderRadius: 4, fontSize: '0.68rem', background: 'rgba(59,130,246,0.1)', color: '#3B82F6', border: '1px solid rgba(59,130,246,0.2)' }}>
                            {allKeyLabels[p] || p}
                          </span>
                        ))}
                        {(role.permissions || []).length > 9 && (
                          <span style={{ whiteSpace: 'nowrap', padding: '2px 7px', borderRadius: 4, fontSize: '0.68rem', color: 'var(--text-muted)', border: '1px solid var(--border)' }}>+{role.permissions.length - 9}</span>
                        )}
                      </div>
                    </td>

                    {/* Membres */}
                    <td style={{ padding: '14px', verticalAlign: 'top', minWidth: 230 }}>
                      {members.length > 0 && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 8 }}>
                          {members.map(u => (
                            <div key={u.id} style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '4px 7px', background: 'var(--bg-elevated)', borderRadius: 6, border: '1px solid var(--border)' }}>
                              <div style={{ width: 22, height: 22, borderRadius: '50%', background: 'rgba(59,130,246,0.15)', color: '#3B82F6', fontSize: '0.58rem', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                {ini(`${u.prenom} ${u.nom}`)}
                              </div>
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontSize: '0.8rem', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{u.prenom} {u.nom}</div>
                                <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{u.email}</div>
                              </div>
                              <button onClick={() => removeMember(u.id)} disabled={saving === u.id}
                                title="Retirer du rôle"
                                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: '2px 3px', borderRadius: 3, opacity: saving === u.id ? 0.4 : 1, flexShrink: 0 }}>
                                <X size={11}/>
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                      <div style={{ display: 'flex', gap: 4 }}>
                        <select value={addSelect[role.id] || ''}
                          onChange={e => setAddSelect(p => ({ ...p, [role.id]: e.target.value }))}
                          style={{ flex: 1, padding: '5px 8px', borderRadius: 5, border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text-primary)', fontSize: '0.78rem', minWidth: 0 }}>
                          <option value="">+ Ajouter un membre…</option>
                          {available.map(u => <option key={u.id} value={u.id}>{u.prenom} {u.nom}</option>)}
                        </select>
                        {addSelect[role.id] && (
                          <button onClick={() => addMember(role.id, addSelect[role.id])} disabled={!!saving}
                            style={{ padding: '5px 8px', borderRadius: 5, border: 'none', background: '#22c55e', color: 'white', cursor: 'pointer', flexShrink: 0 }}>
                            <Check size={12}/>
                          </button>
                        )}
                      </div>
                    </td>

                    {/* Actions */}
                    <td style={{ padding: '14px', verticalAlign: 'top', whiteSpace: 'nowrap' }}>
                      <button onClick={() => setModal(role)} title="Modifier"
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: '4px 6px', borderRadius: 4 }}>
                        <Edit2 size={14}/>
                      </button>
                      <button onClick={() => deleteRole(role)} title="Supprimer"
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', padding: '4px 6px', borderRadius: 4 }}>
                        <Trash2 size={14}/>
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {modal && (
        <RoleFormModal role={modal === 'new' ? null : modal} catalog={catalog}
          onClose={() => setModal(null)} onSaved={fetchRoles}/>
      )}
    </div>
  );
}

// ─── Section Override par utilisateur ─────────────────────────

function UserPermissionsSection({ catalog, users, isSuperAdmin }) {
  const [selectedUserId, setSelectedUserId]   = useState('');
  const [userPerms, setUserPerms]             = useState(null);
  const [roles, setRoles]                     = useState([]);
  const [saving, setSaving]                   = useState(false);
  const [localOverrides, setLocalOverrides]   = useState({}); // key -> bool
  const [localRoleId, setLocalRoleId]         = useState(null);
  const [loadingPerms, setLoadingPerms]       = useState(false);

  useEffect(() => {
    permissionsAPI.listRoles().then(r => setRoles(r.data || [])).catch(() => {});
  }, []);

  const loadUserPerms = useCallback(async (userId) => {
    if (!userId) return;
    setLoadingPerms(true);
    setUserPerms(null);
    setLocalOverrides({});
    setLocalRoleId(null);
    try {
      const res = await permissionsAPI.getUserPerms(userId);
      setUserPerms(res.data);
      setLocalRoleId(res.data.custom_role_id || null);
      // Reconstruire les overrides existants
      const overrideMap = {};
      (res.data.overrides || []).forEach(o => { overrideMap[o.permission_key] = o.is_granted; });
      setLocalOverrides(overrideMap);
    } catch (e) { toast.error(extractError(e)); }
    finally { setLoadingPerms(false); }
  }, []);

  const handleUserChange = (id) => {
    setSelectedUserId(id);
    loadUserPerms(id);
  };

  const toggleOverride = (key, baseHas) => {
    setLocalOverrides(prev => {
      const next = { ...prev };
      if (key in next) {
        // On alterne
        if (next[key] === !baseHas) {
          delete next[key]; // retour à l'état de base
        } else {
          next[key] = !next[key];
        }
      } else {
        next[key] = !baseHas;
      }
      return next;
    });
  };

  const save = async () => {
    if (!selectedUserId) return;
    setSaving(true);
    try {
      const overrides = Object.entries(localOverrides).map(([k, v]) => ({
        permission_key: k, is_granted: v,
      }));
      await permissionsAPI.updateUserPerms(selectedUserId, {
        overrides,
        custom_role_id: localRoleId || null,
      });
      toast.success('Permissions mises à jour');
      loadUserPerms(selectedUserId);
    } catch (e) { toast.error(extractError(e)); }
    finally { setSaving(false); }
  };

  const selectableUsers = users.filter(u => u.role !== 'SUPER_ADMIN' && (isSuperAdmin || u.role !== 'ADMIN'));

  const selectedUser = users.find(u => String(u.id) === String(selectedUserId));

  const basePerms = new Set(userPerms?.base_permissions || []);
  const effectivePerms = new Set(userPerms?.effective_permissions || []);

  const hasKey = (key) => {
    if (key in localOverrides) return localOverrides[key];
    return basePerms.has(key);
  };

  const overrideState = (key) => {
    if (!(key in localOverrides)) return null; // pas d'override
    return localOverrides[key];
  };

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <h3 style={{ margin: '0 0 4px', fontSize: '1rem' }}>Overrides par utilisateur</h3>
        <p style={{ margin: 0, fontSize: '0.8rem', color: 'var(--text-muted)' }}>
          Accordez ou retirez des permissions individuellement, en plus ou en remplacement du rôle custom ou système
        </p>
      </div>

      {/* Sélecteur utilisateur */}
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 20 }}>
        <select
          value={selectedUserId}
          onChange={e => handleUserChange(e.target.value)}
          style={{ flex: 1, maxWidth: 320, padding: '8px 12px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-elevated)', color: 'var(--text-primary)', fontSize: '0.88rem' }}
        >
          <option value="">Sélectionner un utilisateur…</option>
          {selectableUsers.map(u => (
            <option key={u.id} value={u.id}>{u.prenom} {u.nom} — {u.role}</option>
          ))}
        </select>
        {selectedUser && (
          <div style={{ fontSize: '0.82rem', color: 'var(--text-muted)' }}>
            {selectedUser.email}
          </div>
        )}
      </div>

      {loadingPerms && <div style={{ textAlign: 'center', padding: 32 }}><div className="spinner" /></div>}

      {!loadingPerms && userPerms && (
        <div>
          {/* Rôle custom */}
          <div style={{ marginBottom: 16, padding: 14, border: '1px solid var(--border)', borderRadius: 8, background: 'var(--bg-elevated)', display: 'flex', gap: 12, alignItems: 'center' }}>
            <Shield size={16} color="#3B82F6" />
            <label style={{ fontSize: '0.85rem', fontWeight: 600 }}>Rôle custom :</label>
            <select
              value={localRoleId || ''}
              onChange={e => setLocalRoleId(e.target.value || null)}
              style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text-primary)', fontSize: '0.85rem' }}
            >
              <option value="">Aucun (utilise le rôle système)</option>
              {roles.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
            </select>
            <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
              Rôle système : <strong>{selectedUser?.role}</strong>
              {localRoleId && (
                <span style={{ marginLeft: 8, color: '#f59e0b', fontWeight: 600 }}>
                  ⚠ Le rôle custom remplace TOUTES les permissions système
                </span>
              )}
              {!localRoleId && (
                <span style={{ marginLeft: 8, fontStyle: 'italic' }}>
                  (permissions du rôle système actives)
                </span>
              )}
            </span>
          </div>

          {/* Légende */}
          <div style={{ display: 'flex', gap: 16, marginBottom: 12, fontSize: '0.75rem', color: 'var(--text-muted)' }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><span style={{ width: 10, height: 10, borderRadius: 2, background: '#22c55e', display: 'inline-block' }} /> Accordé (base)</span>
            <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><span style={{ width: 10, height: 10, borderRadius: 2, background: '#ef4444', display: 'inline-block' }} /> Retiré (override)</span>
            <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><span style={{ width: 10, height: 10, borderRadius: 2, background: '#3B82F6', border: '2px solid #3B82F6', display: 'inline-block' }} /> Accordé (override)</span>
          </div>

          {/* Grille des modules */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {catalog.map(mod => {
              const anyGranted = mod.keys.some(k => hasKey(k));
              return (
                <div key={mod.key} style={{ border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
                  <div style={{ padding: '10px 14px', background: anyGranted ? 'rgba(59,130,246,0.04)' : 'var(--bg-elevated)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontWeight: 600, fontSize: '0.88rem' }}>{mod.label}</span>
                    {mod.description && <span style={{ fontSize: '0.74rem', color: 'var(--text-muted)' }}>{mod.description}</span>}
                  </div>
                  <div style={{ padding: '10px 14px', display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    {mod.keys.map(key => {
                      const cur = hasKey(key);
                      const ov  = overrideState(key);
                      const base = basePerms.has(key);
                      let bg = 'var(--bg-card)', col = 'var(--text-muted)', border = 'var(--border)';
                      if (ov !== null) {
                        // override actif
                        if (ov) { bg = 'rgba(59,130,246,0.15)'; col = '#3B82F6'; border = '#3B82F6'; }
                        else    { bg = 'rgba(239,68,68,0.12)'; col = '#ef4444'; border = '#ef4444'; }
                      } else if (base) {
                        bg = 'rgba(34,197,94,0.1)'; col = '#22c55e'; border = '#22c55e44';
                      }
                      return (
                        <button
                          key={key}
                          onClick={() => toggleOverride(key, base)}
                          title={`${key} — ${ov !== null ? (ov ? 'override accordé' : 'override retiré') : (base ? 'accordé par le rôle' : 'non accordé')}`}
                          style={{
                            padding: '5px 11px', borderRadius: 6, cursor: 'pointer', fontSize: '0.78rem', fontWeight: 500,
                            background: bg, color: col, border: `1px solid ${border}`, transition: 'all 0.12s',
                          }}
                        >
                          {ov !== null && <span style={{ marginRight: 4, fontWeight: 700 }}>{ov ? '+' : '−'}</span>}
                          {mod.keyLabels[key] || key}
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Actions */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
            <button
              onClick={() => loadUserPerms(selectedUserId)}
              style={{ padding: '8px 16px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-elevated)', cursor: 'pointer', color: 'var(--text-muted)' }}
            >
              Annuler
            </button>
            <button
              onClick={save} disabled={saving}
              style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 18px', borderRadius: 6, border: 'none', background: '#3B82F6', color: 'white', fontWeight: 600, cursor: 'pointer', opacity: saving ? 0.6 : 1 }}
            >
              <Save size={14} /> {saving ? 'Enregistrement...' : 'Sauvegarder'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Composant principal de l'onglet ──────────────────────────

export default function AdminPermissionsTab({ isSuperAdmin }) {
  const [catalog, setCatalog] = useState([]);
  const [users, setUsers]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [section, setSection] = useState('roles');

  const fetchUsers = useCallback(async () => {
    try { const r = await api.get('/admin/users'); setUsers(r.data || []); }
    catch {}
  }, []);

  useEffect(() => {
    const fetchAll = async () => {
      setLoading(true);
      try {
        const [catRes, usersRes] = await Promise.all([
          permissionsAPI.catalog(),
          api.get('/admin/users'),
        ]);
        const cat = (catRes.data || []).map(mod => ({
          key: mod.key,
          label: mod.label,
          description: mod.description,
          keys: mod.permission_keys || [mod.key],
          keyLabels: mod.key_labels || {},
        }));
        setCatalog(cat);
        setUsers(usersRes.data || []);
      } catch (e) { toast.error(extractError(e)); }
      finally { setLoading(false); }
    };
    fetchAll();
  }, []);

  if (loading) return <div style={{ textAlign: 'center', padding: 48 }}><div className="spinner" /></div>;

  return (
    <div>
      {/* Sous-navigation */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 24, borderBottom: '1px solid var(--border)', paddingBottom: 0 }}>
        {[
          { key: 'roles', label: 'Rôles custom' },
          { key: 'overrides', label: 'Overrides individuels' },
        ].map(s => (
          <button
            key={s.key}
            onClick={() => setSection(s.key)}
            style={{
              padding: '8px 18px', border: 'none', background: 'none', cursor: 'pointer',
              borderBottom: section === s.key ? '2px solid #3B82F6' : '2px solid transparent',
              color: section === s.key ? '#3B82F6' : 'var(--text-muted)',
              fontWeight: section === s.key ? 700 : 500, fontSize: '0.88rem', marginBottom: -1,
            }}
          >
            {s.label}
          </button>
        ))}
      </div>

      {section === 'roles' && <CustomRolesSection catalog={catalog} users={users} onUsersRefresh={fetchUsers}/>}
      {section === 'overrides' && <UserPermissionsSection catalog={catalog} users={users} isSuperAdmin={isSuperAdmin} />}
    </div>
  );
}
